<template>
  <AppLayout 
    page-title="Tax Calculator" 
    page-subtitle="Calculate taxes, track deductions, and prepare for tax season"
  >
    <!-- Page Header -->
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        Tax Management
      </div>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Tax Calculator</h1>
      <p class="text-xl text-slate-300 mb-8 max-w-2xl">Calculate your tax liability, track deductions, and prepare quarterly payments</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
        <p class="text-slate-400">Loading tax data...</p>
      </div>
    </div>

    <!-- Tax Overview -->
    <div v-else class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Total Income</p>
          <p class="text-3xl font-bold text-white">${{ taxStats.totalIncome.toLocaleString() }}</p>
          <p class="text-sm text-emerald-400">this year</p>
        </div>
      </div>

      <div class="tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Total Deductions</p>
          <p class="text-3xl font-bold text-white">${{ taxStats.totalDeductions.toLocaleString() }}</p>
          <p class="text-sm text-blue-400">{{ taxStats.deductionPercentage }}% of income</p>
        </div>
      </div>

      <div class="tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Estimated Tax</p>
          <p class="text-3xl font-bold text-white">${{ taxStats.estimatedTax.toLocaleString() }}</p>
          <p class="text-sm text-amber-400">{{ taxStats.effectiveRate }}% effective rate</p>
        </div>
      </div>

      <div class="tax-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Quarterly Due</p>
          <p class="text-3xl font-bold text-white">${{ taxStats.quarterlyDue.toLocaleString() }}</p>
          <p class="text-sm text-purple-400">next payment</p>
        </div>
      </div>
    </div>

    <!-- Tax Calculator Form -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-white">Tax Calculator</h2>
        <button @click="showTaxFormModal = true" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors">
          Update Tax Info
        </button>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Income Section -->
        <div>
          <h3 class="text-lg font-semibold text-white mb-4">Income Sources</h3>
          <div class="space-y-4">
            <div v-for="income in incomeSources" :key="income.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">{{ income.source }}</p>
                <p class="text-sm text-slate-400">{{ income.description }}</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ income.amount.toLocaleString() }}</p>
                <p class="text-sm text-slate-400">{{ income.frequency }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Deductions Section -->
        <div>
          <h3 class="text-lg font-semibold text-white mb-4">Deductions & Credits</h3>
          <div class="space-y-4">
            <div v-for="deduction in deductions" :key="deduction.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
              <div>
                <p class="text-white font-medium">{{ deduction.category }}</p>
                <p class="text-sm text-slate-400">{{ deduction.description }}</p>
              </div>
              <div class="text-right">
                <p class="text-white font-bold">${{ deduction.amount.toLocaleString() }}</p>
                <p class="text-sm text-emerald-400">{{ deduction.type }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quarterly Payments -->
    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-white">Quarterly Tax Payments</h2>
        <button @click="showPaymentModal = true" class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors">
          Schedule Payment
        </button>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-slate-700/50">
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Quarter</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Due Date</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Amount Due</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Status</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-slate-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="payment in quarterlyPayments" :key="payment.id" class="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors">
              <td class="py-4 px-4">
                <p class="text-white font-medium">{{ payment.quarter }}</p>
                <p class="text-sm text-slate-400">{{ payment.year }}</p>
              </td>
              <td class="py-4 px-4">
                <p class="text-white">{{ formatDate(payment.dueDate) }}</p>
              </td>
              <td class="py-4 px-4">
                <p class="text-white font-bold">${{ payment.amountDue.toLocaleString() }}</p>
              </td>
              <td class="py-4 px-4">
                <span :class="getPaymentStatusClass(payment.status)" class="px-3 py-1 rounded-full text-xs font-medium">
                  {{ payment.status }}
                </span>
              </td>
              <td class="py-4 px-4">
                <div class="flex space-x-2">
                  <button @click="viewPaymentDetails(payment)" class="text-blue-400 hover:text-blue-300 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </button>
                  <button v-if="payment.status === 'Pending'" @click="makePayment(payment)" class="text-emerald-400 hover:text-emerald-300 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Tax Documents -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Tax Documents</h3>
        <div class="space-y-4">
          <div v-for="document in taxDocuments" :key="document.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-slate-600/50 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-slate-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">{{ document.name }}</p>
                <p class="text-sm text-slate-400">{{ document.description }}</p>
              </div>
            </div>
            <button @click="generateDocument(document)" class="px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-lg transition-colors">
              Generate
            </button>
          </div>
        </div>
      </div>

      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Quick Actions</h3>
        <div class="space-y-4">
          <button @click="exportTaxData" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Export Tax Data</p>
                <p class="text-sm text-slate-400">Download for tax preparation</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>

          <button @click="scheduleTaxReminder" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Schedule Reminders</p>
                <p class="text-sm text-slate-400">Set tax payment alerts</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>

          <button @click="contactTaxAdvisor" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.08-.041l-.08-.08z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Contact Tax Advisor</p>
                <p class="text-sm text-slate-400">Get professional advice</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Tax Form Modal -->
    <div v-if="showTaxFormModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Update Tax Information</h3>
          <button @click="showTaxFormModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="updateTaxInfo" class="space-y-6">
          <!-- Personal Information -->
          <div>
            <h4 class="text-lg font-semibold text-white mb-4">Personal Information</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Filing Status</label>
                <select v-model="taxForm.filingStatus" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
                  <option value="single">Single</option>
                  <option value="married">Married Filing Jointly</option>
                  <option value="head">Head of Household</option>
                  <option value="separate">Married Filing Separately</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Dependents</label>
                <input v-model.number="taxForm.dependents" type="number" min="0" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              </div>
            </div>
          </div>

          <!-- Income Sources -->
          <div>
            <h4 class="text-lg font-semibold text-white mb-4">Income Sources</h4>
            <div class="space-y-4">
              <div v-for="(income, index) in taxForm.incomeSources" :key="index" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Source</label>
                  <input v-model="income.source" type="text" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="e.g., Contracting">
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Amount</label>
                  <input v-model.number="income.amount" type="number" min="0" step="0.01" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
                </div>
                <div class="flex items-end">
                  <button @click="removeIncomeSource(index)" type="button" class="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
                    Remove
                  </button>
                </div>
              </div>
              <button @click="addIncomeSource" type="button" class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                Add Income Source
              </button>
            </div>
          </div>

          <!-- Deductions -->
          <div>
            <h4 class="text-lg font-semibold text-white mb-4">Deductions</h4>
            <div class="space-y-4">
              <div v-for="(deduction, index) in taxForm.deductions" :key="index" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Category</label>
                  <select v-model="deduction.category" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
                    <option value="business">Business Expenses</option>
                    <option value="vehicle">Vehicle & Mileage</option>
                    <option value="home">Home Office</option>
                    <option value="equipment">Equipment & Tools</option>
                    <option value="insurance">Insurance</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Amount</label>
                  <input v-model.number="deduction.amount" type="number" min="0" step="0.01" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
                </div>
                <div class="flex items-end">
                  <button @click="removeDeduction(index)" type="button" class="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
                    Remove
                  </button>
                </div>
              </div>
              <button @click="addDeduction" type="button" class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                Add Deduction
              </button>
            </div>
          </div>

          <div class="flex space-x-3">
            <button type="button" @click="showTaxFormModal = false" class="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
              Update Tax Info
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Payment Modal -->
    <div v-if="showPaymentModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Schedule Tax Payment</h3>
          <button @click="showPaymentModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="schedulePayment" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Payment Amount</label>
            <input v-model.number="paymentForm.amount" type="number" min="0" step="0.01" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="0.00">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Payment Date</label>
            <input v-model="paymentForm.date" type="date" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Payment Method</label>
            <select v-model="paymentForm.method" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="bank">Bank Transfer</option>
              <option value="credit">Credit Card</option>
              <option value="check">Check</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Notes</label>
            <textarea v-model="paymentForm.notes" rows="3" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="Optional notes..."></textarea>
          </div>
          
          <div class="flex space-x-3">
            <button type="button" @click="showPaymentModal = false" class="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
              Schedule Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import AppLayout from '@/components/layout/AppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { onMounted, ref } from 'vue'

const userStore = useUserStore()

// Types
interface TaxStats {
  totalIncome: number
  totalDeductions: number
  deductionPercentage: number
  estimatedTax: number
  effectiveRate: number
  quarterlyDue: number
}

interface IncomeSource {
  id: number
  source: string
  description: string
  amount: number
  frequency: string
}

interface Deduction {
  id: number
  category: string
  description: string
  amount: number
  type: string
}

interface QuarterlyPayment {
  id: number
  quarter: string
  year: number
  dueDate: string
  amountDue: number
  status: string
}

interface TaxDocument {
  id: number
  name: string
  description: string
  type: string
}

// Reactive data
const isLoading = ref(true)
const showTaxFormModal = ref(false)
const showPaymentModal = ref(false)

const taxStats = ref<TaxStats>({
  totalIncome: 0,
  totalDeductions: 0,
  deductionPercentage: 0,
  estimatedTax: 0,
  effectiveRate: 0,
  quarterlyDue: 0
})

const incomeSources = ref<IncomeSource[]>([])
const deductions = ref<Deduction[]>([])
const quarterlyPayments = ref<QuarterlyPayment[]>([])
const taxDocuments = ref<TaxDocument[]>([])

// Form data
const taxForm = ref({
  filingStatus: 'single',
  dependents: 0,
  incomeSources: [] as any[],
  deductions: [] as any[]
})

const paymentForm = ref({
  amount: 0,
  date: '',
  method: 'bank',
  notes: ''
})

// API Methods
const fetchTaxData = async () => {
  try {
    isLoading.value = true
    
    // Fetch tax statistics
    const statsResponse = await fetch('/api/tax/stats', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (statsResponse.ok) {
      const statsData = await statsResponse.json()
      taxStats.value = statsData
    }
    
    // Fetch income sources
    const incomeResponse = await fetch('/api/tax/income', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (incomeResponse.ok) {
      const incomeData = await incomeResponse.json()
      incomeSources.value = incomeData
    }
    
    // Fetch deductions
    const deductionsResponse = await fetch('/api/tax/deductions', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (deductionsResponse.ok) {
      const deductionsData = await deductionsResponse.json()
      deductions.value = deductionsData
    }
    
    // Fetch quarterly payments
    const paymentsResponse = await fetch('/api/tax/quarterly-payments', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (paymentsResponse.ok) {
      const paymentsData = await paymentsResponse.json()
      quarterlyPayments.value = paymentsData
    }
    
    // Fetch tax documents
    const documentsResponse = await fetch('/api/tax/documents', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (documentsResponse.ok) {
      const documentsData = await documentsResponse.json()
      taxDocuments.value = documentsData
    }
    
  } catch (error) {
    console.error('Error fetching tax data:', error)
    // Fallback to empty data
    taxStats.value = {
      totalIncome: 0,
      totalDeductions: 0,
      deductionPercentage: 0,
      estimatedTax: 0,
      effectiveRate: 0,
      quarterlyDue: 0
    }
    incomeSources.value = []
    deductions.value = []
    quarterlyPayments.value = []
    taxDocuments.value = []
  } finally {
    isLoading.value = false
  }
}

// Methods
const updateTaxInfo = async () => {
  try {
    const response = await fetch('/api/tax/update', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: JSON.stringify(taxForm.value)
    })
    
    if (response.ok) {
      alert('Tax information updated successfully!')
      showTaxFormModal.value = false
      await fetchTaxData() // Refresh data
    } else {
      alert('Error updating tax information')
    }
  } catch (error) {
    console.error('Error updating tax info:', error)
    alert('Error updating tax information')
  }
}

const schedulePayment = async () => {
  try {
    const response = await fetch('/api/tax/schedule-payment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: JSON.stringify(paymentForm.value)
    })
    
    if (response.ok) {
      alert('Payment scheduled successfully!')
      showPaymentModal.value = false
      await fetchTaxData() // Refresh data
    } else {
      alert('Error scheduling payment')
    }
  } catch (error) {
    console.error('Error scheduling payment:', error)
    alert('Error scheduling payment')
  }
}

const makePayment = async (payment: QuarterlyPayment) => {
  try {
    const response = await fetch(`/api/tax/make-payment/${payment.id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (response.ok) {
      alert('Payment processed successfully!')
      await fetchTaxData() // Refresh data
    } else {
      alert('Error processing payment')
    }
  } catch (error) {
    console.error('Error making payment:', error)
    alert('Error processing payment')
  }
}

const generateDocument = async (document: TaxDocument) => {
  try {
    const response = await fetch(`/api/tax/generate-document/${document.id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${document.name}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } else {
      alert('Error generating document')
    }
  } catch (error) {
    console.error('Error generating document:', error)
    alert('Error generating document')
  }
}

const exportTaxData = async () => {
  try {
    // Create CSV data from current tax information
    const csvData = [
      ['Tax Year', '2024'],
      [''],
      ['INCOME SOURCES'],
      ['Source', 'Description', 'Amount', 'Frequency'],
      ...incomeSources.value.map(income => [
        income.source,
        income.description,
        `$${income.amount.toLocaleString()}`,
        income.frequency
      ]),
      [''],
      ['DEDUCTIONS'],
      ['Category', 'Description', 'Amount', 'Type'],
      ...deductions.value.map(deduction => [
        deduction.category,
        deduction.description,
        `$${deduction.amount.toLocaleString()}`,
        deduction.type
      ]),
      [''],
      ['TAX SUMMARY'],
      ['Total Income', `$${taxStats.value.totalIncome.toLocaleString()}`],
      ['Total Deductions', `$${taxStats.value.totalDeductions.toLocaleString()}`],
      ['Deduction Percentage', `${taxStats.value.deductionPercentage}%`],
      ['Estimated Tax', `$${taxStats.value.estimatedTax.toLocaleString()}`],
      ['Effective Rate', `${taxStats.value.effectiveRate}%`],
      ['Next Quarterly Due', `$${taxStats.value.quarterlyDue.toLocaleString()}`],
      [''],
      ['QUARTERLY PAYMENTS'],
      ['Quarter', 'Year', 'Due Date', 'Amount Due', 'Status'],
      ...quarterlyPayments.value.map(payment => [
        payment.quarter,
        payment.year.toString(),
        formatDate(payment.dueDate),
        `$${payment.amountDue.toLocaleString()}`,
        payment.status
      ])
    ]

    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n')

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `tax-data-${new Date().getFullYear()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)

    alert('Tax data exported successfully!')
  } catch (error) {
    console.error('Error exporting tax data:', error)
    alert('Error exporting tax data')
  }
}

const scheduleTaxReminder = async () => {
  try {
    // Get next quarterly payment date
    const nextPayment = quarterlyPayments.value.find(p => p.status === 'Pending')
    
    if (!nextPayment) {
      alert('No pending quarterly payments found')
      return
    }

    // Create reminder data
    const reminderData = {
      type: 'tax_payment',
      title: `Quarterly Tax Payment Due`,
      description: `Payment of $${nextPayment.amountDue.toLocaleString()} due on ${formatDate(nextPayment.dueDate)}`,
      dueDate: nextPayment.dueDate,
      amount: nextPayment.amountDue,
      userId: userStore.currentUser?.id
    }

    const response = await fetch('/api/reminders/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: JSON.stringify(reminderData)
    })

    if (response.ok) {
      const result = await response.json()
      alert(`Tax payment reminder scheduled for ${formatDate(nextPayment.dueDate)}`)
    } else {
      alert('Error scheduling tax reminder')
    }
  } catch (error) {
    console.error('Error scheduling tax reminder:', error)
    alert('Error scheduling tax reminder')
  }
}

const contactTaxAdvisor = async () => {
  try {
    // Get user's tax situation summary
    const taxSummary = {
      totalIncome: taxStats.value.totalIncome,
      totalDeductions: taxStats.value.totalDeductions,
      estimatedTax: taxStats.value.estimatedTax,
      effectiveRate: taxStats.value.effectiveRate,
      quarterlyDue: taxStats.value.quarterlyDue,
      userEmail: userStore.currentUser?.email,
      userName: userStore.currentUser?.name
    }

    // Create consultation request
    const consultationData = {
      type: 'tax_consultation',
      subject: 'Tax Consultation Request',
      message: `I need professional tax advice for my oilfield contracting business. My estimated tax liability is $${taxStats.value.estimatedTax.toLocaleString()} with an effective rate of ${taxStats.value.effectiveRate}%.`,
      taxSummary: taxSummary,
      preferredContact: 'email',
      urgency: 'medium'
    }

    const response = await fetch('/api/support/consultation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: JSON.stringify(consultationData)
    })

    if (response.ok) {
      const result = await response.json()
      alert('Tax consultation request submitted! A tax advisor will contact you within 24 hours.')
    } else {
      alert('Error submitting consultation request')
    }
  } catch (error) {
    console.error('Error contacting tax advisor:', error)
    alert('Error contacting tax advisor')
  }
}

const viewPaymentDetails = (payment: QuarterlyPayment) => {
  console.log('Viewing payment details:', payment)
  alert(`Payment details for ${payment.quarter} ${payment.year}`)
}

const addIncomeSource = () => {
  taxForm.value.incomeSources.push({
    source: '',
    amount: 0
  })
}

const removeIncomeSource = (index: number) => {
  taxForm.value.incomeSources.splice(index, 1)
}

const addDeduction = () => {
  taxForm.value.deductions.push({
    category: 'business',
    amount: 0
  })
}

const removeDeduction = (index: number) => {
  taxForm.value.deductions.splice(index, 1)
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const getPaymentStatusClass = (status: string) => {
  const classes: { [key: string]: string } = {
    'Paid': 'bg-emerald-500/90',
    'Pending': 'bg-yellow-500/90',
    'Overdue': 'bg-red-500/90',
    'Scheduled': 'bg-blue-500/90'
  }
  return classes[status] || 'bg-slate-500/90'
}

// Load data on mount
onMounted(() => {
  fetchTaxData()
})
</script>

<style scoped>
.tax-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/50 transition-all duration-300;
}
</style>
